import { useEffect, useMemo, useState } from 'react'
import {
  Ticket, Search, CalendarDays, Building2, ExternalLink, Copy, Check,
  CheckCircle2, Info, MapPin, Share2
} from 'lucide-react'
import { toast } from '../hooks/useToast'
import { vibrate } from '../utils/appPrefs'

/* =====================================================================
   PARTENARIAT — 500 VOIX POUR JOHNNY (-20 €)
   Même contenu que la page du site :
   https://100pour100johnny.com/500-voix-pour-johnny
   Les prix affichés via ces liens ont déjà les 20 € déduits.
   ===================================================================== */

const BASE = 'https://dhmanagement.trium.fr/fr/r/'
const PAGE_URL = 'https://100pour100johnny.com/500-voix-pour-johnny'

// [ville, code du lien, date, salle] — triés par date
const CITIES = [
  ['Nantes', 'b8fae82ab1', 'Ven. 8 janv. 2027', 'Zénith'],
  ['Rennes', 'e4458b2eab', 'Sam. 9 janv. 2027', 'Le Liberté'],
  ['Tours', '8f62c53c69', 'Dim. 10 janv. 2027', 'Parc des expositions'],
  ['Orléans', '48a4edbd4e', 'Sam. 16 janv. 2027', 'Zénith'],
  ['Amiens', '63dc34d9ad', 'Dim. 17 janv. 2027', 'Zénith'],
  ['Rouen', 'f5a7a191fa', 'Sam. 23 janv. 2027', 'Zénith'],
  ['Caen', '406f288ba4', 'Dim. 24 janv. 2027', 'Zénith'],
  ['Saint-Étienne', '2d340ba592', 'Dim. 31 janv. 2027', 'Zénith'],
  ['Nancy', '613708510f', 'Sam. 6 févr. 2027', 'Zénith'],
  ['Strasbourg', '4dcdce5934', 'Dim. 7 févr. 2027', 'Zénith'],
  ['Narbonne', 'c9ca9a8711', 'Sam. 27 févr. 2027', 'Narbonne Arena'],
  ['Toulouse', 'fa417e036f', 'Dim. 28 févr. 2027', 'Zénith'],
  ['Montbéliard', 'bbddce9bf5', 'Ven. 12 mars 2027', 'Axone'],
  ['Grenoble', 'f6fd4360c3', 'Dim. 14 mars 2027', "L'Auditorium"],
  ['Clermont-Ferrand', 'a712b87584', 'Ven. 19 mars 2027', "Zénith d'Auvergne"],
  ['Lyon', '1cc8ae41dd', 'Sam. 20 mars 2027', 'Halle Tony Garnier'],
  ['Dijon', '2fff5f1461', 'Dim. 21 mars 2027', 'Zénith'],
  ['Épernay', 'ab14986e18', 'Sam. 3 avr. 2027', 'Millesium'],
  ['Trélazé', 'cea3916c74', 'Ven. 21 mai 2027', 'Arena Loire'],
  ['Poitiers', '2f7c142854', 'Sam. 22 mai 2027', 'Arena Futuroscope'],
  ['Limoges', '0e69832311', 'Dim. 23 mai 2027', 'Zénith'],
  ['Pau', '6fc439e31b', 'Sam. 29 mai 2027', 'Zénith'],
  ['Liévin', '70a90c3471', 'Sam. 19 juin 2027', 'Arène Stade Couvert'],
  ['Paris', '73d4837978', 'Dim. 27 juin 2027', 'Accor Arena']
]

const norm = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z]/g, '')

async function copyText(text) {
  try {
    if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return true }
  } catch { /* on tente la méthode de secours */ }
  try {
    const t = document.createElement('textarea')
    t.value = text
    t.style.position = 'fixed'
    t.style.opacity = '0'
    document.body.appendChild(t)
    t.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(t)
    return ok
  } catch { return false }
}

function CityCard({ city, code, date, venue }) {
  const [copied, setCopied] = useState(false)
  const url = BASE + code

  const onCopy = async () => {
    vibrate(8)
    const ok = await copyText(url)
    if (ok) {
      setCopied(true)
      toast.success(`Lien -20 € de ${city} copié`)
      setTimeout(() => setCopied(false), 1800)
    } else {
      toast.error('Copie impossible')
    }
  }

  return (
    <article className="v5-city">
      <div className="v5-city__head">
        <div className="v5-city__name"><MapPin size={17} /> <span>{city}</span></div>
        <span className="v5-tag">-20&nbsp;€</span>
      </div>
      <div className="v5-city__meta">
        <span><CalendarDays size={15} /> {date}</span>
        <span><Building2 size={15} /> {venue}</span>
      </div>
      <div className="v5-city__actions">
        <a
          className="v5-btn"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => vibrate(10)}
        >
          Réserver -20&nbsp;€ <ExternalLink size={16} />
        </a>
        <button
          type="button"
          className={`v5-copy ${copied ? 'is-ok' : ''}`}
          onClick={onCopy}
          aria-label={`Copier le lien de ${city}`}
        >
          {copied ? <Check size={18} /> : <Copy size={18} />}
        </button>
      </div>
      <div className="v5-city__price"><CheckCircle2 size={14} /> Prix affichés 20&nbsp;€ déduits</div>
    </article>
  )
}

function CinqCentsVoixPage() {
  const [q, setQ] = useState('')
  const [showPoster, setShowPoster] = useState(false)

  useEffect(() => { window.scrollTo(0, 0) }, [])

  const list = useMemo(() => {
    const k = norm(q)
    if (!k) return CITIES
    return CITIES.filter(([city, , , venue]) => norm(city + venue).includes(k))
  }, [q])

  const share = async () => {
    vibrate(8)
    const data = {
      title: '500 Voix pour Johnny – 20 € de réduction',
      text: '20 € de réduction sur le concert 500 Voix pour Johnny avec Radio 100% Johnny 🎤',
      url: PAGE_URL
    }
    try {
      if (navigator.share) { await navigator.share(data); return }
    } catch { return }
    if (await copyText(PAGE_URL)) toast.success('Lien de la page copié')
  }

  return (
    <div className="v5">
      {/* EN-TÊTE */}
      <header className="v5-hero">
        <span className="v5-kicker">Offre partenaire · Radio 100% Johnny</span>
        <h1 className="v5-title">500 Voix pour <span>Johnny</span></h1>

        <button type="button" className="v5-visual" onClick={() => setShowPoster(true)} aria-label="Voir l'affiche avec toutes les dates">
          <img src="/images/500-voix-carre.jpg" alt="500 Voix pour Johnny – 10 ans après – En tournée dans toute la France" />
          <span className="v5-visual__hint">Voir toutes les dates</span>
        </button>

        <div className="v5-deal">
          <strong>-20&nbsp;€</strong>
          <span>
            de réduction sur votre billet
            <small>Prix affichés avec les 20&nbsp;€ déjà déduits, via le lien de votre ville</small>
          </span>
        </div>

        <a href="#v5-villes" className="v5-btn v5-btn--big" onClick={(e) => {
          e.preventDefault()
          document.getElementById('v5-villes')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }}>
          <Ticket size={20} /> Choisir ma ville
        </a>
      </header>

      {/* COMMENT ÇA MARCHE */}
      <section className="v5-section">
        <h2 className="v5-h2">Comment obtenir vos 20&nbsp;€ de réduction</h2>
        <p className="v5-lead">
          Pas de code à saisir : le lien de votre ville vous emmène directement vers la billetterie,
          où <b>les prix indiqués sont déjà réduits de 20&nbsp;€</b>.
        </p>
        <ol className="v5-steps">
          <li><span>1</span><div><b>Trouvez votre ville</b>parmi les 24 villes de la tournée 2027.</div></li>
          <li><span>2</span><div><b>Touchez « Réserver -20&nbsp;€ »</b>les prix indiqués ont déjà les 20&nbsp;€ déduits.</div></li>
          <li><span>3</span><div><b>Chantez avec Johnny</b>validez votre commande et rendez-vous au concert.</div></li>
        </ol>
      </section>

      {/* VILLES */}
      <section className="v5-section" id="v5-villes">
        <h2 className="v5-h2">Choisissez votre ville</h2>
        <p className="v5-lead">Chaque ville a son propre lien : utilisez bien celui de la ville où vous allez au concert.</p>

        <label className="v5-search">
          <Search size={18} />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Ville ou salle (ex : Lyon)"
            aria-label="Rechercher une ville ou une salle"
          />
        </label>
        <div className="v5-count"><b>{list.length}</b> ville{list.length > 1 ? 's' : ''} · 20&nbsp;€ de réduction dans chacune</div>

        <div className="v5-grid">
          {list.map(([city, code, date, venue]) => (
            <CityCard key={code} city={city} code={code} date={date} venue={venue} />
          ))}
        </div>
        {list.length === 0 && <div className="v5-empty">Aucune ville ne correspond à votre recherche.</div>}

        <div className="v5-note">
          <Info size={20} />
          <div>
            <b>Important :</b> les prix indiqués sur la billetterie via ces liens sont <b>déjà réduits de 20&nbsp;€</b>,
            il n'y a rien à déduire au moment du paiement. La réduction s'applique uniquement en réservant via ces liens partenaires.
          </div>
        </div>
      </section>

      {/* PARTAGE */}
      <section className="v5-section">
        <div className="v5-share">
          <h2 className="v5-h2" style={{ marginTop: 0 }}>Faites profiter vos amis</h2>
          <p className="v5-lead">Partagez l'offre : chacun pourra réserver avec 20&nbsp;€ de réduction dans sa ville.</p>
          <button type="button" className="v5-btn v5-btn--big" onClick={share}><Share2 size={19} /> Partager l'offre</button>
        </div>
      </section>

      {/* AFFICHE PLEIN ÉCRAN */}
      {showPoster && (
        <div className="v5-lightbox" role="dialog" aria-modal="true" aria-label="Affiche de la tournée" onClick={() => setShowPoster(false)}>
          <img src="/images/500-voix-affiche.jpg" alt="Affiche de la tournée 500 Voix pour Johnny avec toutes les dates" />
          <button type="button" className="v5-lightbox__close">Fermer</button>
        </div>
      )}

      <style>{`
        .v5{min-height:100vh;background:#0a0a0a;color:#fff;padding-bottom:120px;box-sizing:border-box;line-height:1.55}
        .v5 *{box-sizing:border-box}
        .v5-hero{padding:28px 16px 8px;text-align:center;
          background:radial-gradient(ellipse at top,rgba(220,20,60,.32),transparent 65%)}
        .v5-kicker{display:inline-block;font-size:.68rem;font-weight:800;letter-spacing:1.4px;text-transform:uppercase;
          color:#ffd700;padding:6px 12px;border-radius:20px;border:1px solid rgba(255,215,0,.4);background:rgba(0,0,0,.35)}
        .v5-title{font-size:1.9rem;font-weight:900;margin:14px 0 16px;line-height:1.1}
        .v5-title span{color:#ffd700}
        .v5-visual{position:relative;display:block;width:100%;max-width:420px;margin:0 auto;padding:0;border:1px solid rgba(220,20,60,.5);
          border-radius:20px;overflow:hidden;background:#000;cursor:pointer;box-shadow:0 16px 40px rgba(220,20,60,.25)}
        .v5-visual img{display:block;width:100%;height:auto}
        .v5-visual__hint{position:absolute;left:50%;bottom:12px;transform:translateX(-50%);background:rgba(0,0,0,.75);
          color:#ffd700;font-weight:700;font-size:.8rem;padding:6px 14px;border-radius:20px;white-space:nowrap}
        .v5-deal{display:flex;align-items:center;gap:14px;max-width:420px;margin:18px auto;padding:14px 16px;border-radius:18px;text-align:left;
          background:linear-gradient(135deg,#dc143c,#8b0000);border:2px solid rgba(255,215,0,.6)}
        .v5-deal strong{font-size:2.5rem;font-weight:900;line-height:1;color:#ffd700;white-space:nowrap}
        .v5-deal span{font-weight:800;font-size:1rem}
        .v5-deal small{display:block;font-weight:500;font-size:.8rem;color:#ffe3e7;margin-top:3px;line-height:1.35}
        .v5-btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;padding:12px 14px;border-radius:999px;border:none;
          background:linear-gradient(135deg,#ffd700,#ffed4e);color:#111;font-weight:800;font-size:.95rem;text-decoration:none;
          white-space:nowrap;cursor:pointer;font-family:inherit}
        .v5-btn:active{transform:scale(.97)}
        .v5-btn--big{width:100%;max-width:420px;padding:15px 18px;font-size:1.02rem}
        .v5-section{padding:22px 16px 4px}
        .v5-h2{font-size:1.3rem;font-weight:900;text-align:center;margin:6px 0 8px;line-height:1.25}
        .v5-lead{color:rgba(255,255,255,.72);text-align:center;font-size:.92rem;margin:0 auto 16px;max-width:460px}
        .v5-lead b{color:#ffd700}
        .v5-steps{list-style:none;margin:0 auto;padding:0;display:grid;gap:10px;max-width:460px}
        .v5-steps li{display:flex;gap:12px;align-items:flex-start;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.1);
          border-radius:16px;padding:14px;font-size:.9rem;color:rgba(255,255,255,.7)}
        .v5-steps li > span{flex:0 0 32px;height:32px;border-radius:50%;display:grid;place-items:center;font-weight:900;color:#111;
          background:linear-gradient(135deg,#ffd700,#ffed4e)}
        .v5-steps b{display:block;color:#fff;font-size:.98rem}
        .v5-search{display:flex;align-items:center;gap:10px;max-width:460px;margin:0 auto;padding:0 14px;border-radius:14px;
          border:1px solid rgba(255,215,0,.35);background:rgba(0,0,0,.5);color:#ffd700}
        .v5-search input{flex:1;min-width:0;background:transparent;border:none;outline:none;color:#fff;font-size:16px;padding:13px 0;font-family:inherit}
        .v5-count{text-align:center;color:rgba(255,255,255,.6);font-size:.82rem;margin:10px 0 14px}
        .v5-count b{color:#ffd700}
        .v5-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(250px,1fr));gap:12px;max-width:900px;margin:0 auto}
        .v5-city{display:flex;flex-direction:column;gap:10px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.1);
          border-radius:18px;padding:16px;min-width:0}
        .v5-city__head{display:flex;align-items:center;justify-content:space-between;gap:8px}
        .v5-city__name{display:flex;align-items:center;gap:8px;font-size:1.12rem;font-weight:800;min-width:0}
        .v5-city__name svg{color:#dc143c;flex-shrink:0}
        .v5-tag{flex-shrink:0;font-size:.72rem;font-weight:900;background:#dc143c;padding:4px 9px;border-radius:999px;white-space:nowrap}
        .v5-city__meta{display:flex;flex-direction:column;gap:3px;font-size:.85rem;color:rgba(255,255,255,.65)}
        .v5-city__meta span{display:flex;align-items:center;gap:7px}
        .v5-city__meta svg{color:#ffd700;flex-shrink:0}
        .v5-city__actions{display:flex;gap:8px}
        .v5-city__actions .v5-btn{flex:1;min-width:0}
        .v5-copy{flex:0 0 46px;height:46px;border-radius:50%;border:1px solid rgba(255,215,0,.4);background:transparent;color:#ffd700;
          display:grid;place-items:center;cursor:pointer}
        .v5-copy.is-ok{background:#1f8f4e;border-color:#1f8f4e;color:#fff}
        .v5-city__price{display:flex;align-items:center;gap:6px;font-size:.78rem;font-weight:700;color:#7be0a2}
        .v5-empty{text-align:center;color:rgba(255,255,255,.6);padding:24px;border:1px dashed rgba(255,255,255,.15);border-radius:16px}
        .v5-note{display:flex;gap:12px;align-items:flex-start;max-width:900px;margin:18px auto 0;padding:14px 16px;border-radius:16px;
          background:rgba(255,215,0,.08);border:1px solid rgba(255,215,0,.35);color:rgba(255,255,255,.85);font-size:.88rem}
        .v5-note svg{color:#ffd700;flex-shrink:0;margin-top:2px}
        .v5-share{text-align:center;padding:22px 16px;border-radius:20px;border:1px solid rgba(255,255,255,.1);
          background:linear-gradient(135deg,rgba(220,20,60,.18),rgba(255,215,0,.05));max-width:900px;margin:0 auto}
        .v5-lightbox{position:fixed;inset:0;z-index:9999;background:rgba(0,0,0,.92);display:flex;flex-direction:column;
          align-items:center;justify-content:center;gap:14px;padding:16px}
        .v5-lightbox img{max-width:100%;max-height:82vh;border-radius:12px}
        .v5-lightbox__close{padding:11px 26px;border-radius:999px;border:1px solid #ffd700;background:transparent;color:#ffd700;font-weight:800;font-size:1rem}
      `}</style>
    </div>
  )
}

export default CinqCentsVoixPage
